import http, {
  AUTH_LOGIN_KEY,
  AUTH_LOGOUT_KEY,
  AUTH_RESET_PASSWORD_KEY,
  AUTH_SCHEMA_KEY,
  endpoints,
  HEADER_USER_ID_KEY,
  IS_ESOFT_USER_KEY,
  RESET_PASSWORD_CODE_KEY,
} from '../http';
import { mapUserFromDto } from '../users/helpers';
import { IDatahubAuthDto, IGenerateShineTokenResponseDto } from './dtos';
import { LocalizedError } from 'localization';
import { IUser } from '../users/models';
import { jwtDecode } from 'jwt-decode';
import { DecodedJWTType, StorageHeaderValueType } from '../types';
import { IUserDto } from '../users/dtos';
import { IBranchDto } from '../department/dtos';
import appRoutes from 'app.routes';
import { MAIN_USER_ENDPOINT } from '../../constants/server/apiEndPoint';

const datahubClient = http.createClient(`${endpoints.datahub}`);

export default class AuthApi {
  static isAuthenticated(): boolean {
    return !!http.getAuthToken();
  }

  static deauthenticate() {
    http.forgetAuthToken();
  }

  static getUserFromToken() {
    const token = http.getAuthToken();
    return token && jwtDecode<DecodedJWTType>(token);
  }

  static async getAuthenticatedUser(options?: { abortSignal?: AbortSignal }): Promise<IUser | undefined> {
    const token = http.getAuthToken();

    if (!token) {
      return undefined;
    }

    const authUserResponse = await datahubClient.get<IUserDto>(MAIN_USER_ENDPOINT);

    const authUser = authUserResponse?.data;

    if (!authUser?.email) {
      throw new LocalizedError('auth.pages.activeDirectory.errorGettingUser');
    }

    const branchesResponse = await datahubClient.get<IBranchDto[]>('/Authentication/user/branches');

    const detailedBranches = branchesResponse?.data || [];
    const userResponse = await datahubClient.get<IUserDto | ''>(
      `/User/id/${authUser.currentlyImpersonatingUserId || authUser.id}`,
      {
        signal: options?.abortSignal,
      }
    );

    const user = userResponse?.data;

    if (!user) {
      if (window.location.pathname !== appRoutes.activeDirectory()) {
        window.location.pathname = appRoutes.activeDirectory();
      }
      return;
    }

    const resultBranches =
      user?.branches?.map<IBranchDto>(branch => {
        const foundDetailedBranch = detailedBranches.find(detailedBranch => detailedBranch.id === branch.id);

        if (foundDetailedBranch) {
          return {
            ...foundDetailedBranch,
            ...branch,
            country: branch.countryCode || branch.country || foundDetailedBranch.country,
          };
        }

        return branch;
      }) || [];

    return mapUserFromDto({ ...authUser, phone: user.phone, settings: user.settings }, resultBranches);
  }

  static async getShineToken() {
    const token = http.getAuthToken();

    const dto: IGenerateShineTokenResponseDto = {
      TokenValue: token || '',
      HashValue: '',
    };

    return dto;
  }

  static async b2cAuth(email: string, appRoute: string) {
    const response = await datahubClient.get<IDatahubAuthDto>(
      `/Authentication/login-url?redirectUrl=${`${window.location.origin}${appRoute}`}&email=${email}&nonce=esoft-test`
    );

    const data = response?.data;

    localStorage.setItem(IS_ESOFT_USER_KEY, 'false');
    localStorage.setItem(AUTH_SCHEMA_KEY, data?.authenticationScheme);
    localStorage.setItem(AUTH_LOGIN_KEY, data?.loginUrl);
    localStorage.setItem(AUTH_LOGOUT_KEY, data?.logoutUrl);
    localStorage.setItem(AUTH_RESET_PASSWORD_KEY, data?.resetPasswordUrl);
    data?.resetPasswordErrorCode && localStorage.setItem(RESET_PASSWORD_CODE_KEY, data.resetPasswordErrorCode);
    localStorage.setItem(HEADER_USER_ID_KEY, JSON.stringify({ header: data?.impersonatingUserIdHeaderName }));

    return data;
  }

  static async esoftAuth(appRoute: string) {
    const response = await datahubClient.get<IDatahubAuthDto>(
      `/Authentication/login-esoft-url?redirectUrl=${`${window.location.origin}${appRoute}`}&nonce=esoft-test`
    );
    const data = response?.data;
    localStorage.setItem(IS_ESOFT_USER_KEY, 'true');
    localStorage.setItem(AUTH_SCHEMA_KEY, data?.authenticationScheme);
    localStorage.setItem(AUTH_LOGIN_KEY, data?.loginUrl);
    localStorage.setItem(AUTH_LOGOUT_KEY, data?.logoutUrl);
    localStorage.setItem(AUTH_RESET_PASSWORD_KEY, data?.resetPasswordUrl);
    data?.resetPasswordErrorCode && localStorage.setItem(RESET_PASSWORD_CODE_KEY, data.resetPasswordErrorCode);
    localStorage.setItem(HEADER_USER_ID_KEY, JSON.stringify({ header: data?.impersonatingUserIdHeaderName }));

    return data;
  }

  static persistToken(token: string) {
    http.setAuthToken(token, false);
  }

  static clearAuthHeaders() {
    try {
      const userIdHeader: StorageHeaderValueType = JSON.parse(localStorage.getItem(HEADER_USER_ID_KEY) || '');
      userIdHeader?.value && localStorage.setItem(HEADER_USER_ID_KEY, JSON.stringify({ header: userIdHeader.header }));
    } catch (ex) {
      // noop
    }
  }
}
